import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHead } from '../components/bits.tsx';
import {
  Alert,
  Button,
  EmptyState,
  Modal,
  Panel,
  SelectField,
  TableSkeleton,
  TextAreaField,
  TextField,
  useToast,
} from '../components/ui/index.tsx';
import { api } from '../lib/api.ts';
import { describeError } from '../lib/errors.ts';
import { formatMoney } from '../lib/money.ts';
import { useSession } from '../state/session.tsx';
import type { PartyKind, PartyWithStats, StateRef } from '../lib/types.ts';
import { CountryField } from '../components/CountryField.tsx';

export function Parties({ kind }: { kind: PartyKind }) {
  const { business, canWrite } = useSession();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['parties', business?.id, kind, search],
    queryFn: () =>
      api.get<{ parties: PartyWithStats[] }>('/api/parties', {
        kind,
        search: search || undefined,
        limit: 200,
      }),
    enabled: Boolean(business),
  });

  const parties = data?.parties ?? [];
  const noun = kind === 'customer' ? 'Customer' : 'Vendor';

  return (
    <>
      <PageHead
        title={`${noun}s`}
        lede={
          kind === 'customer'
            ? 'Everyone you bill. Their tax details drive how each invoice is taxed.'
            : 'Everyone you buy from, for purchase orders and expenses.'
        }
        actions={
          canWrite && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New {noun.toLowerCase()}
            </Button>
          )
        }
      />

      <div className="toolbar">
        <input
          className="input toolbar__search"
          type="search"
          placeholder={`Search ${noun.toLowerCase()}s`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          aria-label={`Search ${noun.toLowerCase()}s`}
        />
      </div>

      <Panel flush>
        {isLoading ? (
          <TableSkeleton rows={5} columns={4} />
        ) : parties.length === 0 ? (
          <EmptyState
            mark={kind === 'customer' ? '◍' : '◌'}
            title={search ? 'Nothing matches that search' : `No ${noun.toLowerCase()}s yet`}
            lede={
              search
                ? undefined
                : `Add your first ${noun.toLowerCase()} so you can start raising documents.`
            }
            action={
              canWrite && !search ? (
                <Button variant="primary" onClick={() => setCreating(true)}>
                  Add one
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Tax ID</th>
                  <th>Location</th>
                  {kind === 'customer' && <th className="num">Invoiced</th>}
                  {kind === 'customer' && <th className="num">Owed</th>}
                </tr>
              </thead>
              <tbody>
                {parties.map((party) => (
                  <tr
                    key={party.id}
                    className="row-link"
                    tabIndex={0}
                    onClick={() => navigate(`/parties/${party.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') navigate(`/parties/${party.id}`);
                    }}
                  >
                    <td className="strong">
                      {party.name}
                      {party.email && (
                        <span className="faint" style={{ display: 'block', fontSize: 'var(--text-xs)' }}>
                          {party.email}
                        </span>
                      )}
                    </td>
                    <td className="muted">{party.taxId ?? '—'}</td>
                    <td className="muted">
                      {[party.billingCity, party.stateCode, party.country]
                        .filter(Boolean)
                        .join(', ') || '—'}
                    </td>
                    {kind === 'customer' && (
                      <td className="num">
                        {formatMoney(party.invoicedMinor, business?.currency ?? 'INR')}
                      </td>
                    )}
                    {kind === 'customer' && (
                      <td className="num strong">
                        {party.outstandingMinor > 0 ? (
                          <span style={{ color: 'var(--warn)' }}>
                            {formatMoney(party.outstandingMinor, business?.currency ?? 'INR')}
                          </span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {creating && <PartyModal kind={kind} onClose={() => setCreating(false)} />}
    </>
  );
}

export function PartyModal({
  kind,
  party,
  onClose,
}: {
  kind: PartyKind;
  party?: PartyWithStats;
  onClose: () => void;
}) {
  const { business } = useSession();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const isIndia = business?.taxRegime === 'IN_GST';

  const [form, setForm] = useState({
    name: party?.name ?? '',
    contactName: party?.contactName ?? '',
    email: party?.email ?? '',
    phone: party?.phone ?? '',
    taxId: party?.taxId ?? '',
    country: party?.country ?? business?.country ?? 'IN',
    stateCode: party?.stateCode ?? '',
    billingLine1: party?.billingLine1 ?? '',
    billingCity: party?.billingCity ?? '',
    billingPostalCode: party?.billingPostalCode ?? '',
    paymentTermsDays: String(party?.paymentTermsDays ?? ''),
    notes: party?.notes ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  const { data: jurisdictions } = useQuery({
    queryKey: ['jurisdictions'],
    queryFn: () => api.get<{ indiaStates: StateRef[] }>('/api/reference/jurisdictions'),
    enabled: isIndia,
    staleTime: Infinity,
  });

  const set = (patch: Partial<typeof form>) => setForm((current) => ({ ...current, ...patch }));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        kind,
        paymentTermsDays: form.paymentTermsDays ? Number(form.paymentTermsDays) : null,
      };
      return party
        ? api.patch(`/api/parties/${party.id}`, body)
        : api.post('/api/parties', body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['parties'] });
      void queryClient.invalidateQueries({ queryKey: ['party'] });
      notify(party ? 'Saved' : `${kind === 'customer' ? 'Customer' : 'Vendor'} added`, 'success');
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Modal
      wide
      title={party ? `Edit ${party.name}` : `New ${kind}`}
      lede={
        isIndia
          ? 'A GSTIN sets the state automatically, which decides how tax is split.'
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack stack-4">
        {error && <Alert tone="error">{error}</Alert>}

        <div className="form-grid">
          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => set({ name: event.target.value })}
            autoFocus
            required
          />
          <TextField
            label="Contact person"
            value={form.contactName}
            onChange={(event) => set({ contactName: event.target.value })}
          />
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(event) => set({ email: event.target.value })}
          />
          <TextField
            label="Phone"
            value={form.phone}
            onChange={(event) => set({ phone: event.target.value })}
          />
          <TextField
            label={isIndia ? 'GSTIN' : business?.taxRegime === 'AE_VAT' ? 'TRN' : 'Tax ID'}
            value={form.taxId}
            onChange={(event) => set({ taxId: event.target.value.toUpperCase() })}
            placeholder={isIndia ? '29AAGCB7383J1Z4' : undefined}
          />
          <CountryField
            value={form.country}
            onChange={(code) => set({ country: code })}
            hint="A different country makes the supply an export."
          />
          {isIndia ? (
            <SelectField
              label="State"
              value={form.stateCode}
              onChange={(event) => set({ stateCode: event.target.value })}
            >
              <option value="">Not set</option>
              {jurisdictions?.indiaStates.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.code} — {state.name}
                </option>
              ))}
            </SelectField>
          ) : (
            <TextField
              label="State / region"
              value={form.stateCode}
              onChange={(event) => set({ stateCode: event.target.value })}
            />
          )}
          <TextField
            label="Payment terms (days)"
            numeric
            inputMode="numeric"
            value={form.paymentTermsDays}
            onChange={(event) => set({ paymentTermsDays: event.target.value })}
            placeholder={String(business?.defaultPaymentTermsDays ?? 15)}
          />
        </div>

        <div className="form-grid">
          <TextField
            label="Address"
            value={form.billingLine1}
            onChange={(event) => set({ billingLine1: event.target.value })}
          />
          <TextField
            label="City"
            value={form.billingCity}
            onChange={(event) => set({ billingCity: event.target.value })}
          />
          <TextField
            label="Postal code"
            value={form.billingPostalCode}
            onChange={(event) => set({ billingPostalCode: event.target.value })}
          />
        </div>

        <TextAreaField
          label="Notes"
          value={form.notes}
          onChange={(event) => set({ notes: event.target.value })}
          rows={2}
        />
      </div>
    </Modal>
  );
}
